import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ChatFrame } from '../components';
import { ChatFrameConstants } from '../constants';
import { ChatBlkProvider } from '../providers';
import { ChatBlkCustomProps } from '../types';

const wrapper = document.createElement('div') as HTMLElement;
const shadowRoot = wrapper.attachShadow({
    mode: 'open',
});
wrapper.id = ChatFrameConstants.CHAT_CANVAS_WRAPPER;
const cache = createCache({
    key: 'chatblk-styles',
    prepend: true,
    container: shadowRoot,
});

/**
 * ChatBlk component for rendering the chat canvas.
 * @param props - Optional properties for the ChatBlk component.
 * @returns {JSX.Element | null} A JSX element containing the chat canvas if the target element is ready,
 *   or null if the target element is not yet established.
 */
const ChatBlk = (props?: ChatBlkCustomProps): JSX.Element => {
    const hostHeaderRef = useRef<HTMLElement | null>(null);
    const shadowHostRef = useRef<HTMLDivElement>(null);
    const { appContext = null } = props || {};
    // State to hold the target element where the chat canvas will be injected
    const [target, setTarget] = useState<HTMLElement | null>(null);

    /**
     * Calculates the height of the provided header element.
     * @param headerElement - The header HTML element from which to measure the height.
     * @returns The height of the header element in pixels, or 0 if the element is undefined.
     */
    const getHeaderOffset = useCallback(
        (headerElement: HTMLElement | null): number => {
            return Math.min(
                headerElement?.getBoundingClientRect().bottom || 0,
                0
            );
        },
        []
    );

    /**
     * Performs the setup for the ChatBLK micro frontend by creating or selecting the chat canvas wrapper,
     * styling it appropriately, and positioning it relative to the provided header element.
     */
    const setup = useCallback(() => {
        if (hostHeaderRef.current) {
            Object.assign(wrapper.style, {
                position: 'fixed',
                height: '100%',
                right: '0',
                bottom: '0',
                zIndex: '1005',
            });

            hostHeaderRef.current?.appendChild(wrapper);

            setTarget(wrapper as unknown as HTMLElement);
        }
    }, [getHeaderOffset]);

    useEffect(() => {
        // Find the body element to append the chat canvas wrapper
        hostHeaderRef.current = document.body;
        if (!hostHeaderRef.current) {
            console.warn(
                '[ChatBlk] No header found. Chat canvas will not be initialized.'
            );
            return;
        }
        setup();
    }, []);

    useEffect(() => {
        if (shadowHostRef.current && !shadowHostRef.current.shadowRoot) {
            const shadowRoot = shadowHostRef.current.attachShadow({
                mode: 'open',
            });
            const style = document.createElement('style');
            style.textContent = `
            :host {
                display: contents;
            }
        `;
            shadowRoot.appendChild(style);
        }
    }, [target]);

    if (!target) return null;
    // Render the ChatBlkProvider and ChatFrame components into the target element,
    // this is the true entry point for the ChatBlk application.
    return createPortal(
        <CacheProvider value={cache}>
            <ChatBlkProvider appContext={appContext}>
                <ChatFrame hostHeaderRef={hostHeaderRef} wrapperRef={wrapper} />
            </ChatBlkProvider>
        </CacheProvider>,
        target.shadowRoot
    );
};

ChatBlk.displayName = 'ChatBlk Host Injector';

export default ChatBlk;
